// plan-10 / plan-14 S02: stage controller — single-widget invariant + chrome rails

import type { StageWidget, StageHandle, StageCtx } from './types'
import { set } from '../../core/save'
import { play } from '../../audio/sfx'

const FADE_MS = 120

function prefersReducedMotion(): boolean {
  return document.body.classList.contains('vt-reduced-motion')
}

interface CurrentWidget {
  widget: StageWidget
  handle: StageHandle
}

export class Stage {
  current: CurrentWidget | null = null
  host: HTMLElement
  registry: Map<string, StageWidget> = new Map()
  headerLabel: HTMLElement
  headerIdChip: HTMLElement
  bodySlot: HTMLElement
  closeButton: HTMLElement
  idleLine: HTMLElement
  wrapper: HTMLElement
  headerRailEl: HTMLElement
  footerRailEl: HTMLElement

  constructor(wrapper: HTMLElement) {
    this.wrapper = wrapper
    this.host = wrapper
    this.headerLabel = wrapper.querySelector('.vt-stage-header-label')!
    this.headerIdChip = wrapper.querySelector('.vt-stage-header-id')!
    this.bodySlot = wrapper.querySelector('.vt-stage-body')!
    this.closeButton = wrapper.querySelector('.vt-stage-close')!
    this.idleLine = wrapper.querySelector('.vt-stage-idle')!
    this.headerRailEl = wrapper.querySelector('.vt-stage-rail-header')!
    this.footerRailEl = wrapper.querySelector('.vt-stage-rail-footer')!

    this.closeButton.addEventListener('click', () => this.dismiss())
  }

  register(widget: StageWidget): void {
    this.registry.set(widget.id, widget)
  }

  activate(id: string, args: string[] = []): void {
    const widget = this.registry.get(id)
    if (!widget) return

    if (this.current?.widget.id === id) {
      this.current.handle.onSwap?.(args)
      return
    }

    const reduced = prefersReducedMotion()

    if (this.current) {
      this.teardownCurrent(reduced)
    }

    this.bodySlot.innerHTML = ''
    // idle is now detached; ensure flag is set (not relied on display)
    this.headerLabel.textContent = widget.label
    this.headerIdChip.textContent = `◇ ${widget.id}`
    this.closeButton.style.display = ''

    const ctx: StageCtx = {
      args,
      setLabel: (label: string) => {
        this.headerLabel.textContent = label
      },
      emitOracle: (line: string) => {
        const history = document.querySelector('.vt-history') as HTMLElement | null
        if (history) {
          const el = document.createElement('div')
          el.className = 'vt-line-oracle'
          el.style.color = 'var(--vt-green-dim)'
          el.textContent = line
          history.appendChild(el)
        }
      },
      close: () => this.dismiss(),
      headerRail: () => this.headerRailEl,
      footerRail: () => this.footerRailEl,
    }

    const handle = widget.mount(this.bodySlot, ctx)
    this.current = { widget, handle }

    if (id === 'directory') {
      set('stageLast', id)
    } else {
      set('stageLast', null)
    }

    if (!reduced) {
      this.bodySlot.style.opacity = '0'
      requestAnimationFrame(() => {
        this.bodySlot.style.transition = `opacity ${FADE_MS}ms ease`
        this.bodySlot.style.opacity = '1'
      })
      play('command_accept')
    } else {
      this.bodySlot.style.opacity = '1'
      play('command_accept')
    }

    this.emitChanged()
  }

  toggle(id: string, args: string[] = []): void {
    if (this.current?.widget.id === id) {
      this.dismiss()
    } else {
      this.activate(id, args)
    }
  }

  dismiss(): void {
    if (!this.current) return

    const reduced = prefersReducedMotion()
    this.teardownCurrent(reduced)

    this.bodySlot.innerHTML = ''
    this.headerLabel.textContent = 'STAGE // IDLE'
    this.headerIdChip.textContent = ''
    this.closeButton.style.display = 'none'
    // re-attach idle line (innerHTML cleared it)
    this.idleLine.style.display = ''
    this.idleLine.textContent = '// stage: idle. consensus: probably.'
    this.bodySlot.appendChild(this.idleLine)
    this.bodySlot.style.opacity = '1'

    // clear rails of any widget-specific items (persistent items keep)
    this.footerRailEl.querySelectorAll('[data-rail-owner]').forEach((el) => el.remove())
    this.headerRailEl.querySelectorAll('[data-rail-owner]').forEach((el) => el.remove())
    this.refreshRailVisibility()

    set('stageLast', null)
    this.emitChanged()
  }

  isActive(id: string): boolean {
    return this.current?.widget.id === id
  }

  refreshRailVisibility(): void {
    this.headerRailEl.classList.toggle(
      'vt-stage-rail-active',
      this.headerRailEl.children.length > 0,
    )
    this.footerRailEl.classList.toggle(
      'vt-stage-rail-active',
      this.footerRailEl.children.length > 0,
    )
  }

  private teardownCurrent(reduced: boolean): void {
    if (!this.current) return

    if (!reduced) {
      this.bodySlot.style.transition = `opacity ${FADE_MS}ms ease`
      this.bodySlot.style.opacity = '0'
    }

    this.current.handle.dismiss()
    this.current = null

    this.headerRailEl.querySelectorAll('[data-rail-owner]').forEach((el) => el.remove())
    this.footerRailEl.querySelectorAll('[data-rail-owner]').forEach((el) => el.remove())
    this.refreshRailVisibility()
  }

  private emitChanged(): void {
    // plan-19 S01: toggle vt-stage-active body class + data-stage-widget for ratio swap on narrow/compact
    const id = this.current?.widget.id ?? null
    if (id) {
      document.body.classList.add('vt-stage-active')
      document.body.dataset.stageWidget = id
    } else {
      document.body.classList.remove('vt-stage-active')
      delete document.body.dataset.stageWidget
    }
    window.dispatchEvent(new CustomEvent('stage:changed', {
      detail: { id },
    }))
    this.syncNodesBtn()
  }

  private syncNodesBtn(): void {
    const btn = document.getElementById('vt-nodes-toggle')
    if (!btn) return
    const isActive = this.current?.widget.id === 'directory'
    btn.textContent = isActive ? '[ - dir ]' : '[ + dir ]'
  }
}

let stageInstance: Stage | null = null

export function createStage(wrapper: HTMLElement): Stage {
  stageInstance = new Stage(wrapper)
  return stageInstance
}

export function getStage(): Stage | null {
  return stageInstance
}
